import React, { useMemo } from 'react';
import Button from '../components/Inputs/Button/Button';
import { PencilLine, Trash2 } from 'lucide-react';
import GenericListView from '../views/GenericListView';
import FilterContainer from '../containers/FilterContainer';
import ModalContainer from '../containers/ModalContainer';
import useModalController from '../hooks/useModalController';
import useListController from '../hooks/useListController';
import useDialogCopy from '../hooks/useDialogCopy';
import {
    createGeoUnit,
    readGeoUnits,
    updateGeoUnit,
    deleteGeoUnit,
    listGeoLevels,
    listGeoParents,
    listGeoParentsForEdit,
} from '../api/geoUnits';


export default function GeoUnitsPage() {
    const controller = useListController({
        defaults: {
            page: 1,
            size: 10,
            sortBy: 'id',
            sortDir: 'asc',
            filters: { level: 'All', parentName: 'All' },
        },
        createFn: createGeoUnit,
        readFn: readGeoUnits,
        updateFn: updateGeoUnit,
        deleteFn: deleteGeoUnit,
    });


    const modalCtl = useModalController({
        create: controller.actions.create,
        update: controller.actions.update,
        remove: controller.actions.remove,
        mapEditValues: (row) => ({
            name: row.name,
            level: row.level,
            postCode: row.postCode,
            parentId: row.parentId,
        }),
    });


    const columns = useMemo(() => ([
        { key: 'id', label: 'Unit ID' },
        { key: 'name', label: 'Name' },
        { key: 'level', label: 'Level' },
        { key: 'postCode', label: 'Post Code' },
        { key: 'parentName', label: 'Parent Unit' },
    ]), []);


    const filterConfig = useMemo(() => ([
        { key: 'level', label: 'Level', type: 'select', allValue: 'All', loadOptions: listGeoLevels },
        { key: 'parentName', label: 'Parent Unit', type: 'select', allValue: 'All', loadOptions: listGeoParents },
    ]), []);


    const header = useMemo(() => (
        <FilterContainer
            filters={filterConfig}
            state={controller.state}
            setFilter={controller.actions.setFilter}
            setSearch={controller.actions.setSearch}
            search={{ key: 'q', label: 'Search by name' }}
        />
    ), [controller.state, controller.actions.setFilter, controller.actions.setSearch, filterConfig]);


    const geoUnitFields = useMemo(() => ([
        { key: 'name', label: 'Name', type: 'input', required: true },
        { key: 'level', label: 'Level', type: 'select', required: true, loadOptions: listGeoLevels },
        { key: 'postCode', label: 'Post Code', type: 'input', placeholder: 'Number', inputType: 'text' },
        { key: 'parentId', label: 'Parent Unit', type: 'select', loadOptions: listGeoParentsForEdit },
    ]), []);


    const actionCell = (row) => (
        <>
            <Button
                variant="warning" shape="square" leadingIcon={<PencilLine size={18} />}
                onClick={() => modalCtl.actions.openEdit(row)}
            >Edit</Button>


            <Button
                variant="danger" shape="square" leadingIcon={<Trash2 size={18} />}
                onClick={() => modalCtl.actions.openDelete(row)}
            >Delete</Button>
        </>
    );


    const actions = (
        <Button variant="primary" shape="pill" onClick={modalCtl.actions.openCreate}>
            Add New
        </Button>
    );


    const { open, mode, entity, values } = modalCtl.modal;
    const copy = useDialogCopy({ entityLabel: 'Geographic Unit', mode, entity });
    const fields = useMemo(() => (mode === 'delete' ? [] : geoUnitFields), [mode, geoUnitFields]);


    return (
        <>
            <GenericListView
                title="Geographic Unit"
                subtitle="View, search, filter and add the different geographical units available in the system"
                actions={actions}
                header={header}
                columns={columns}
                tableProps={controller.tableProps}
                paginationProps={controller.paginationProps}
                actionCell={actionCell}
                actionHeader="Actions"
            />
            <ModalContainer
                open={open}
                mode={mode}
                title={copy.title}
                subtitle={copy.subtitle}
                yesLabel={copy.yesLabel}
                yesVariant={copy.yesVariant}
                fields={fields}
                values={values}
                setValue={modalCtl.actions.setValue}
                onNo={modalCtl.actions.close}
                onYes={modalCtl.actions.commit}
            />
        </>
    );
}